import React, { useState, useEffect } from 'react'
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-light'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx'
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript'
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript'
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python'
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash'
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json'
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css'
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml'
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown'
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql'

SyntaxHighlighter.registerLanguage('tsx', tsx)
SyntaxHighlighter.registerLanguage('typescript', typescript)
SyntaxHighlighter.registerLanguage('ts', typescript)
SyntaxHighlighter.registerLanguage('javascript', javascript)
SyntaxHighlighter.registerLanguage('js', javascript)
SyntaxHighlighter.registerLanguage('python', python)
SyntaxHighlighter.registerLanguage('py', python)
SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('sh', bash)
SyntaxHighlighter.registerLanguage('shell', bash)
SyntaxHighlighter.registerLanguage('json', json)
SyntaxHighlighter.registerLanguage('css', css)
SyntaxHighlighter.registerLanguage('yaml', yaml)
SyntaxHighlighter.registerLanguage('yml', yaml)
SyntaxHighlighter.registerLanguage('markdown', markdown)
SyntaxHighlighter.registerLanguage('md', markdown)
SyntaxHighlighter.registerLanguage('sql', sql)
import { useUserStore } from '../store/userStore'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp?: number
}

interface ChatMessageProps {
  message: Message
  animate?: boolean
}

// react-markdown v10 strips data: URIs by default. Allow them through.
const urlTransform = (url: string) =>
  url.startsWith('data:') ? url : defaultUrlTransform(url)

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
      aria-label="Copy code"
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  )
}

const markdownComponents: Components = {
  // Code blocks with syntax highlighting
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '')
    const codeString = String(children).replace(/\n$/, '')

    if (match) {
      return (
        <div className="relative my-3 rounded-lg overflow-hidden border border-gray-700 shadow-sm group">
          <div className="flex items-center justify-between bg-gray-800 px-3 py-1.5 border-b border-gray-700">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{match[1]}</span>
          </div>
          <CopyButton text={codeString} />
          <SyntaxHighlighter
            style={oneDark}
            language={match[1]}
            PreTag="div"
            customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.8rem', lineHeight: '1.5' }}
            showLineNumbers={codeString.split('\n').length > 5}
          >
            {codeString}
          </SyntaxHighlighter>
        </div>
      )
    }
    return (
      <code className="bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono border border-blue-100" {...props}>
        {children}
      </code>
    )
  },

  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto my-3 rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-gray-700 border-t border-gray-100">{children}</td>,
  tr: ({ children }) => <tr className="hover:bg-gray-50/50 transition-colors">{children}</tr>,

  // Images — handles base64 data URIs, external URLs, any src
  img: ({ src, alt, ...props }) => (
    <div className="my-3">
      <img
        src={src}
        alt={alt || 'Image'}
        className="max-w-full rounded-lg shadow-md border border-gray-200"
        loading="lazy"
        {...props}
      />
      {alt && alt !== 'Image' && (
        <p className="text-xs text-gray-500 mt-1 text-center italic">{alt}</p>
      )}
    </div>
  ),

  // Links
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors">
      {children}
    </a>
  ),

  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-4 border-blue-400 bg-blue-50/50 pl-4 pr-3 py-2 rounded-r-lg text-gray-700 italic">
      {children}
    </blockquote>
  ),

  // Lists
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-gray-700 leading-relaxed">{children}</li>,

  // Headings
  h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2 text-gray-900 border-b border-gray-200 pb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold mt-3 mb-2 text-gray-900">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-1 text-gray-800">{children}</h3>,

  // Paragraphs
  p: ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,

  // Horizontal rules
  hr: () => <hr className="my-4 border-gray-200" />,
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user'
  const { name } = useUserStore()
  const userInitial = name ? name.charAt(0).toUpperCase() : 'U'
  const [displayedContent, setDisplayedContent] = useState(message.content)

  useEffect(() => {
    setDisplayedContent(message.content)
  }, [message.content])

  const formatTimestamp = (timestamp: number): string => {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(new Date(timestamp))
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2 sm:mb-4`}>
      {!isUser && (
        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold mr-1 sm:mr-2 flex-shrink-0 shadow-sm">
          <span className="text-[10px] sm:text-xs">ED</span>
        </div>
      )}
      <div
        className={`max-w-[85%] sm:max-w-[80%] rounded-xl sm:rounded-2xl p-2 sm:p-4 shadow-sm ${
          isUser
            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
            : 'bg-white border border-gray-100 text-gray-800'
        }`}
      >
        {message.timestamp && (
          <div className={`text-[9px] sm:text-[10px] ${isUser ? 'text-white/70' : 'text-gray-400'} mb-1 text-right`}>
            {formatTimestamp(message.timestamp)}
          </div>
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm sm:text-base">{message.content}</div>
        ) : (
          <div className="rich-content text-xs sm:text-sm">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              urlTransform={urlTransform}
              components={markdownComponents}
            >
              {displayedContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
      {isUser && (
        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-bold ml-1 sm:ml-2 flex-shrink-0 shadow-sm">
          <span className="text-[10px] sm:text-xs">{userInitial}</span>
        </div>
      )}
    </div>
  )
}
